import clsx from 'clsx';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import type { MouseEvent } from 'react';
import { lazy, memo, Suspense, useEffect, useMemo, useState } from 'react';

import type { RelevancyCardProps } from '../../DynamicCardComponent';
import { useRelevancyFeed } from '../../model';
import { useRelevancyOptions } from '../../store';
import BaseCardComponent from '../BaseCardComponent';

import { useWindowSize } from '@hooks/useWindowSize';
import { trackCardElement } from '@services/GoogleTagManagerService';
import { trackedVideoKinds } from '@services/JwPlayerService';
import { getVideoUrl } from '@services/LinkRuleService';
import { useSponsor } from '@shared/RandomSponsors/useSponsor';
import { useVideoFromMas } from '@shared/VideoPlayer/hooks/useMediaAssetService';

import * as styles from './styles.module.scss';

const VideoDialog = lazy(() => import(/* webpackPrefetch: true */ './VideoDialog'));

dayjs.extend(duration);
dayjs.extend(relativeTime);

export const VideoComponent = (props: RelevancyCardProps) => {
  const {
    item,
    item: { teaser },
  } = props;
  const { options } = useRelevancyOptions();
  const { isMobile } = useWindowSize();
  const { toggleCardHidden } = useRelevancyFeed();
  const [openOverlay, setOpenOverlay] = useState(false);
  const kind = teaser.classification?.kind?.toLowerCase();

  const sponsorName = useMemo(
    () => !options.disableSponsors && (teaser.sponsor?.type ?? teaser.sponsors?.at(0)),
    [options.disableSponsors, teaser.sponsor, teaser.sponsors],
  );

  const { sponsor } = useSponsor(sponsorName);

  const isSponsoredCard = useMemo(() => !options.disableSponsors && !!sponsor, [options.disableSponsors, sponsor]);

  const trackingData = useMemo(
    () => ({
      name: [isSponsoredCard && sponsor?.code, teaser.title].filter(Boolean).join(' - '),
      id: teaser.id,
      category: 'video card',
      variant: trackedVideoKinds.includes(kind) ? kind : 'video',
      item_category3: sponsor?.code ? 'sponsored' : undefined,
      item_category4: sponsor?.code,
    }),
    [isSponsoredCard, kind, sponsor?.code, teaser.id, teaser.title],
  );

  const videoDuration = useMemo(() => {
    const duration = teaser.duration ?? teaser.mediaDuration;

    if (!duration) return '';

    if (duration >= 3600) {
      return dayjs.duration(duration, 's').format('HH:mm:ss');
    }

    return dayjs.duration(duration, 's').format('mm:ss');
  }, [teaser.duration, teaser.mediaDuration]);

  const availableVideo = useVideoFromMas(teaser);
  useEffect(() => {
    toggleCardHidden(item, !availableVideo);
  }, [availableVideo, item, toggleCardHidden]);

  const link = useMemo(
    () => teaser?.targetUrl ?? getVideoUrl(teaser?.id) ?? teaser?.webReference,
    [teaser?.id, teaser?.targetUrl, teaser?.webReference],
  );

  const height = teaser?.height ?? teaser?.mediaHeight;
  const width = teaser?.width ?? teaser?.mediaWidth;

  const isVerticalVideo = useMemo(() => Boolean(height && width && height > width), [height, width]);
  const videoPlayerInPlace = options.design === 'mosaic-single';
  const cardFormat = useMemo(() => {
    if (isVerticalVideo && (isMobile || props.cardFormat !== 'wide')) return 'portrait';
    return props.cardFormat;
  }, [isMobile, isVerticalVideo, props.cardFormat]);

  const handleCardClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (!isVerticalVideo && !videoPlayerInPlace) return;

    e.preventDefault();
    setOpenOverlay(true);
  };

  const isVideoPlayer = useMemo(() => {
    return (isVerticalVideo && isMobile) || videoPlayerInPlace;
  }, [isMobile, isVerticalVideo, videoPlayerInPlace]);

  return (
    <>
      <div
        className={clsx(
          'card',
          isVerticalVideo && styles.containerVertical,
          videoPlayerInPlace && styles.mosaicSingleVideo,
        )}
        ref={trackCardElement(trackingData)}
        data-event-label='video'
      >
        <BaseCardComponent
          {...props}
          title={teaser.title}
          url={isVerticalVideo ? null : link}
          cardContent={isVerticalVideo || videoPlayerInPlace ? 'over' : null}
          theme={isVerticalVideo || videoPlayerInPlace ? 'dark' : null}
          sponsorCode={sponsor?.code}
          cardFormat={cardFormat}
          isVideoCard={!videoPlayerInPlace}
          isVideoPlayer={isVideoPlayer}
          video={(isVerticalVideo && isMobile) || videoPlayerInPlace ? teaser : null}
          onClick={handleCardClick}
          videoDuration={videoDuration}
          image={teaser.mainMedia?.image}
          disableLink={isVideoPlayer}
          showRoofline={options.showRoofline}
        />
      </div>

      {isVerticalVideo && (
        <Suspense>
          <VideoDialog teaser={teaser} openOverlay={openOverlay} setOpenOverlay={setOpenOverlay} />
        </Suspense>
      )}
    </>
  );
};

export default memo(VideoComponent);
